import { Component } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";

/**
 * App-wide crash guard. Without this, any uncaught render-time error
 * anywhere in the tree unmounts the whole React app to a blank white
 * screen (React 16+ default) — or, in CRA's dev server, surfaces as the
 * raw "Uncaught runtime errors" overlay with a full JS stack trace,
 * which is fine for a developer but must never be what a real customer
 * sees. This boundary catches both cases and renders a plain, branded
 * "Something went wrong" screen instead.
 *
 * Scope note: React error boundaries only catch errors thrown during
 * render / lifecycle methods / constructors of their child tree — NOT
 * errors inside event handlers or rejected promises (e.g. a failed
 * ``api.get(...)`` call). Those are a separate, already-handled class
 * of error in this codebase: every API call already wraps failures in
 * try/catch + a ``toast.error(...)`` (see any page's ``catch`` blocks),
 * so they never crash the render tree in the first place — this
 * boundary exists specifically for the cases that DO crash rendering
 * (a bad prop shape, a null-deref in a component, etc).
 */
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // Real production logging channel is whatever the dev is already
        // using to debug native builds this session — Chrome/Safari
        // remote debugging or `adb logcat` — both surface console.error.
        // No new backend telemetry endpoint exists in this codebase yet;
        // wiring one up is a separate decision, not implied by "log
        // internally for debugging".
        console.error("[ErrorBoundary] Uncaught render error:", error, info?.componentStack);
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <div
                data-testid="app-error-boundary"
                className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-background"
            >
                <span className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 inline-flex items-center justify-center mb-4">
                    <AlertTriangle size={26} />
                </span>
                <h1 className="text-lg font-display font-bold">Something went wrong</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                    We hit an unexpected error. Please try again — if it keeps happening, restarting the app usually helps.
                </p>
                <button
                    data-testid="app-error-boundary-reload"
                    onClick={() => window.location.reload()}
                    className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold text-sm px-5 py-2.5 rounded-xl active:scale-95 transition"
                >
                    <RefreshCw size={15} /> Try again
                </button>
            </div>
        );
    }
}
